import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useActiveLab,
  useAllOrderTasks,
  useAllProjectTasks,
} from '@witmetrics/data-store';
import { filterUnisonTasks } from '@witmetrics/utils/filters';
import { getRoundedDateTime } from '@witmetrics/utils/time';
import { useAppState } from '@/providers/AppStateProvider';
import { useMountEffect } from '@/hooks/useMountEffect';
import Box, { type BoxProps } from '@/components/Box';
import HourBlock from './HourBlock';
import AgendaEvent from './AgendaEvent';
import CurrentTime from './CurrentTime';
import {
  classes,
  getCurrentTasks,
  getHours,
  getOffsetHeight,
  isOffHour,
  sortEvents,
} from './utils';

export type AgendaProps = {
  className?: string;
  style?: BoxProps['sx'];
  showLabels?: boolean;
  autoScroll?: boolean;
  showCurrentTime?: boolean;
  showCurrentTimeDot?: boolean;
  statusFilters?: boolean[];
  userFilters?: number[];
  hourHeight?: number;
  offsetTop?: string;
  date: string;
};

export default function Agenda({
  className,
  style = {},
  showLabels = true,
  autoScroll = true,
  showCurrentTime = true,
  showCurrentTimeDot = true,
  statusFilters = [],
  userFilters = [],
  hourHeight = 75,
  offsetTop = '3rem', // Account for anything changing the parent wrapper's sizing. 3rem = py-12
  date,
}: AgendaProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { onStartTask } = useAppState();
  const activeLab = useActiveLab();
  const projectTasks = useAllProjectTasks();
  const orderTasks = useAllOrderTasks();
  const currentTasks = getCurrentTasks(
    date,
    activeLab ? orderTasks : projectTasks
  );

  useMountEffect(() => {
    if (wrapperRef.current && autoScroll) {
      wrapperRef.current.scroll({ top: getOffsetHeight(hourHeight) });
    }
  });

  const handleStartTask = (hour: number, minute: number) => {
    let taskDate = new Date(date);
    taskDate.setHours(hour, minute, 0);
    onStartTask(getRoundedDateTime(taskDate.toISOString()));
  };

  return (
    <Box
      ref={wrapperRef}
      sx={style}
      className={twMerge(classes.wrapper, className)}>
      {getHours().map((h) => (
        <HourBlock
          key={h}
          showLabels={showLabels}
          offHours={isOffHour(h, date)}
          hour={h}
          height={hourHeight}
          onStartTask={handleStartTask}
        />
      ))}
      {sortEvents(
        filterUnisonTasks(currentTasks, statusFilters, userFilters),
        showLabels ? 70 : 0
      ).map(({ agendaTask, style }) => (
        <AgendaEvent
          key={agendaTask.task.id}
          style={style}
          hourHeight={hourHeight}
          agendaTask={agendaTask}
          offsetTop={offsetTop}
        />
      ))}
      {showCurrentTime && (
        <CurrentTime
          showDot={showCurrentTimeDot}
          offsetTop={offsetTop}
          hourHeight={hourHeight}
        />
      )}
    </Box>
  );
}
